/** @jsxImportSource @emotion/react */
import {
  Datepicker,
  Input,
  Modal,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { CaseCustomerType, useCreateCase } from 'api/useCasesApi';

interface Props {
  customerId: string;
  customerType: CaseCustomerType;
  onClose: () => void;
}

export const CreateCaseModal = ({
  customerId,
  customerType,
  onClose,
}: Props) => {
  const { spacing, palette } = useTheme();
  const [caseTitle, setCaseTitle] = useState('');
  const [caseNote, setCaseNote] = useState('');
  const [caseWakeupAt, setCaseWakeupAt] = useState<string>('');
  const createCaseMutation = useCreateCase();

  const resetForm = () => {
    setCaseTitle('');
    setCaseNote('');
    setCaseWakeupAt('');
  };

  const handleClose = () => {
    if (!createCaseMutation.isPending) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal
      title="Create Case"
      closeModal={handleClose}
      primaryButtonProps={{
        onClick: () => {
          createCaseMutation.mutate(
            {
              customerId,
              customerType,
              title: caseTitle,
              wakeupAt: caseWakeupAt ? new Date(caseWakeupAt) : undefined,
              note: caseNote,
            },
            {
              onSuccess: () => {
                resetForm();
                onClose();
              },
            }
          );
        },
        text: 'Create',
        loading: createCaseMutation.isPending,
        disabled: !caseTitle || !caseNote,
      }}
      secondaryButtonProps={{
        onClick: handleClose,
        text: 'Cancel',
        disabled: createCaseMutation.isPending,
      }}
    >
      <div css={{ display: 'grid', gap: spacing.xxs, minWidth: '500px' }}>
        <Input
          type="text"
          label="Title"
          value={caseTitle}
          onChange={(e) => setCaseTitle(e.target.value)}
        />
        <Input
          type="textarea"
          label="Note"
          value={caseNote}
          onChange={(e) => setCaseNote(e.target.value)}
          rows={3}
        />
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="bodySmallest" color={palette.grey.x500}>
            Postpone until (optional)
          </Typography>
          <div>
            <Datepicker
              title="Postpone until"
              value={caseWakeupAt ? new Date(caseWakeupAt) : null}
              onChange={(date: Date | null) => {
                setCaseWakeupAt(date ? date.toISOString() : '');
              }}
              variant="primary"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
